import React, { useMemo } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  Button,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
} from "reactstrap";

import { entityFormKey } from "../../../helpers/entityEditForm";
import { ClientTag } from "../../../types/clientTag";

export interface TagFormValues {
  name: string;
  color: string;
  is_active: boolean;
}

interface TagFormModalProps {
  isOpen: boolean;
  editingTag: ClientTag | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (values: TagFormValues) => Promise<void>;
}

const emptyFormValues = (): TagFormValues => ({
  name: "",
  color: "",
  is_active: true,
});

const mapTagToFormValues = (tag: ClientTag): TagFormValues => ({
  name: tag.name,
  color: tag.color ?? "",
  is_active: tag.is_active,
});

const TagFormModal = ({
  isOpen,
  editingTag,
  submitting,
  onClose,
  onSubmit,
}: TagFormModalProps) => {
  const { t } = useTranslation();

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string()
          .trim()
          .required(t("settings.client_tags.validation.nameRequired"))
          .max(100, t("settings.client_tags.validation.nameMax")),
        color: Yup.string()
          .trim()
          .matches(/^#?[A-Fa-f0-9]{6}$|^$/, t("settings.client_tags.validation.colorInvalid")),
        is_active: Yup.boolean(),
      }),
    [t]
  );

  const formik = useFormik<TagFormValues>({
    enableReinitialize: true,
    initialValues: editingTag ? mapTagToFormValues(editingTag) : emptyFormValues(),
    validationSchema,
    onSubmit: async (values, helpers) => {
      await onSubmit(values);
      helpers.setSubmitting(false);
    },
  });

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>
        {editingTag
          ? t("settings.client_tags.form.editTitle")
          : t("settings.client_tags.form.createTitle")}
      </ModalHeader>
      <ModalBody>
        <Form
          key={entityFormKey(editingTag?.id)}
          onSubmit={(event) => {
            event.preventDefault();
            formik.handleSubmit();
          }}
        >
          <div className="mb-3">
            <Label htmlFor="client-tag-name">{t("settings.client_tags.form.name")}</Label>
            <Input
              id="client-tag-name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={!!(formik.touched.name && formik.errors.name)}
            />
            {formik.touched.name && formik.errors.name ? (
              <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
            ) : null}
          </div>

          <div className="mb-3">
            <Label htmlFor="client-tag-color">{t("settings.client_tags.form.color")}</Label>
            <div className="d-flex gap-2 align-items-center">
              <Input
                type="color"
                id="client-tag-color-picker"
                value={
                  formik.values.color && /^#?[A-Fa-f0-9]{6}$/.test(formik.values.color)
                    ? formik.values.color.startsWith("#")
                      ? formik.values.color
                      : `#${formik.values.color}`
                    : "#6C757D"
                }
                onChange={(event) =>
                  formik.setFieldValue("color", event.target.value.toUpperCase())
                }
                style={{ width: 48, height: 38, padding: 2 }}
              />
              <Input
                id="client-tag-color"
                name="color"
                placeholder="#3B82F6"
                value={formik.values.color}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.color && formik.errors.color)}
              />
            </div>
            {formik.touched.color && formik.errors.color ? (
              <FormFeedback type="invalid" className="d-block">
                {formik.errors.color}
              </FormFeedback>
            ) : (
              <small className="text-muted">{t("settings.client_tags.form.colorHint")}</small>
            )}
          </div>

          {editingTag ? (
            <div className="form-check form-switch mb-3">
              <Input
                type="checkbox"
                className="form-check-input"
                id="client-tag-active"
                checked={formik.values.is_active}
                onChange={(event) => formik.setFieldValue("is_active", event.target.checked)}
              />
              <Label className="form-check-label" htmlFor="client-tag-active">
                {t("settings.client_tags.form.active")}
              </Label>
            </div>
          ) : null}

          <div className="d-flex justify-content-end gap-2">
            <Button type="button" color="light" onClick={onClose} disabled={submitting}>
              {t("settings.client_tags.actions.cancel")}
            </Button>
            <Button type="submit" color="primary" disabled={submitting}>
              {submitting ? <Spinner size="sm" /> : t("settings.client_tags.actions.save")}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default TagFormModal;
